import { useEffect, useState } from 'react'
import { ChevronRight, FileDown, Plus, X } from 'lucide-react'
import {
  formatContacts,
  parseContacts,
  type Contact,
  type FaqEntry,
  type Showcase,
  type ShowcaseInput,
} from '@k4ia/shared'
import {
  createShowcase,
  deleteAsset,
  deletePartnerLogo,
  deleteShowcase,
  getShowcase,
  listShowcases,
  partnerLogoUrl,
  updateShowcase,
  uploadAsset,
  uploadPartnerLogo,
  type AdminAsset,
} from '../lib/api'
import { CameraTest } from '../components/CameraTest'
import { showcaseAusWord } from '../lib/showcaseImport'
import { BackButton, FileButton } from '../components/ui'
import { BILD_KANTE, BILD_NICHT_LESBAR, REFERENZ_KANTE, verkleinereBild } from '../lib/bild'

const EMPTY_FORM: ShowcaseInput = {
  slug: '',
  title: '',
  shortText: '',
  longText: '',
  targetAudience: '',
  keyMessages: [],
  useCases: [],
  faq: [],
  tabooRules: [],
  visualDescription: '',
  partnerName: '',
  contactPerson: '',
  status: 'draft',
  sortOrder: 0,
}

export function ShowcasesPage() {
  const [showcases, setShowcases] = useState<Showcase[]>([])
  const [uploadFehler, setUploadFehler] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [assets, setAssets] = useState<AdminAsset[]>([])
  /** R2-Schlüssel des Partner-Logos — nicht Teil des Formulars, eigener Upload. */
  const [partnerLogo, setPartnerLogo] = useState<string | null>(null)
  /** „Aus Word übernehmen“: woher das Formular gerade kommt und was dabei auffiel. */
  const [wordImport, setWordImport] = useState<{ datei: string; hinweise: string[] } | null>(null)
  const [wordFehler, setWordFehler] = useState<string | null>(null)
  const [logoFehler, setLogoFehler] = useState<string | null>(null)
  const [form, setForm] = useState<ShowcaseInput>(EMPTY_FORM)
  const [status, setStatus] = useState<'idle' | 'saving' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  /**
   * Am Handy passen Liste und Formular nicht nebeneinander: Wer einen
   * Showcase antippt, sieht nur noch das Formular, „Zurück“ bringt die Liste.
   * Ab lg stehen beide nebeneinander, dann ist das egal.
   */
  const [editing, setEditing] = useState(false)
  /** Word-Export: was gerade passiert, oder `null`, wenn nichts läuft. */
  const [export_, setExport] = useState<{ id: string | null; text: string } | null>(null)
  const [exportFehler, setExportFehler] = useState<string | null>(null)

  async function exportieren(id: string | null) {
    setExportFehler(null)
    setExport({ id, text: 'Starte …' })
    try {
      // Erst beim Klick laden: Die Word-Bibliothek ist groß, gebraucht wird sie selten.
      const { showcasesAlsWord } = await import('../lib/showcaseExportLaden')
      await showcasesAlsWord(id, (text) => setExport({ id, text }))
    } catch (err) {
      setExportFehler(`Export fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`)
    } finally {
      setExport(null)
    }
  }

  async function refreshList() {
    const { showcases } = await listShowcases()
    setShowcases(showcases)
  }

  useEffect(() => {
    void refreshList()
  }, [])

  /*
   * Das Formular hat eine eigene Adresse (`#/showcases/<id>` bzw. `/neu`):
   * Die Zurück-Geste am Handy schließt es, statt das Admin zu verlassen, und
   * neu laden öffnet denselben Showcase wieder.
   */
  useEffect(() => {
    function fromHash() {
      const sub = window.location.hash.replace(/^#\/?/, '').split('/')[1]
      if (!sub) {
        setEditing(false)
        return
      }
      setEditing(true)
      void selectShowcase(sub === 'neu' ? null : decodeURIComponent(sub))
    }
    fromHash()
    window.addEventListener('popstate', fromHash)
    return () => window.removeEventListener('popstate', fromHash)
  }, [])

  async function selectShowcase(id: string | null) {
    setSelectedId(id)
    setWordImport(null)
    setErrorMessage(null)
    setSaved(false)
    if (!id) {
      setForm(EMPTY_FORM)
      setAssets([])
      setPartnerLogo(null)
      return
    }
    const { showcase, assets } = await getShowcase(id)
    setForm({
      slug: showcase.slug,
      title: showcase.title,
      shortText: showcase.shortText,
      longText: showcase.longText,
      targetAudience: showcase.targetAudience,
      keyMessages: showcase.keyMessages,
      useCases: showcase.useCases,
      faq: showcase.faq,
      tabooRules: showcase.tabooRules,
      visualDescription: showcase.visualDescription,
      partnerName: showcase.partnerName,
      contactPerson: showcase.contactPerson ?? '',
      status: showcase.status,
      sortOrder: showcase.sortOrder,
    })
    setAssets(assets)
    setPartnerLogo(showcase.partnerLogoKey)
    setLogoFehler(null)
  }

  /**
   * Ein Word-Dokument im Aufbau des Exports ins Formular. Gibt es den Slug
   * schon, wird jener Showcase geöffnet und überschrieben — sonst ein neuer,
   * ans Ende der Liste sortiert. Gespeichert wird erst mit „Speichern“.
   */
  async function ausWordUebernehmen(file: File) {
    setWordFehler(null)
    try {
      const { form: gelesen, hinweise } = await showcaseAusWord(await file.arrayBuffer())
      const vorhanden = showcases.find((s) => s.slug === gelesen.slug)
      if (vorhanden) {
        open(vorhanden.id, false)
        await selectShowcase(vorhanden.id)
        // Status, Sortierung und Partner bleiben, wie sie im Admin stehen.
        setForm((alt) => ({
          ...gelesen,
          status: alt.status,
          sortOrder: alt.sortOrder,
          partnerName: alt.partnerName,
        }))
        hinweise.unshift(
          `„${vorhanden.title}“ gibt es schon (Slug ${gelesen.slug}) — die Felder kommen aus Word, Status und Sortierung bleiben.`,
        )
      } else {
        open(null, false)
        await selectShowcase(null)
        const ende = showcases.reduce((m, s) => Math.max(m, s.sortOrder), 0) + 1
        setForm({ ...gelesen, sortOrder: ende })
      }
      setWordImport({ datei: file.name, hinweise })
    } catch (err) {
      setWordFehler(err instanceof Error ? err.message : String(err))
    }
  }

  async function logoHochladen(file: File) {
    if (!selectedId) return
    setLogoFehler(null)
    try {
      setPartnerLogo((await uploadPartnerLogo(selectedId, file)).partnerLogoKey)
    } catch (err) {
      setLogoFehler(err instanceof Error ? err.message : String(err))
    }
  }

  async function logoEntfernen() {
    if (!selectedId) return
    setLogoFehler(null)
    try {
      await deletePartnerLogo(selectedId)
      setPartnerLogo(null)
    } catch (err) {
      setLogoFehler(err instanceof Error ? err.message : String(err))
    }
  }

  async function handleSave() {
    setStatus('saving')
    setErrorMessage(null)
    try {
      if (selectedId) {
        await updateShowcase(selectedId, form)
      } else {
        const { id } = await createShowcase(form)
        setSelectedId(id)
        window.history.replaceState(
          window.history.state,
          '',
          `#/showcases/${encodeURIComponent(id)}`,
        )
      }
      await refreshList()
      setStatus('idle')
      setSaved(true)
    } catch (err) {
      setStatus('error')
      setErrorMessage(err instanceof Error ? err.message : 'Speichern fehlgeschlagen')
    }
  }

  async function handleDelete() {
    if (!selectedId || !confirm('Diesen Showcase wirklich löschen?')) return
    await deleteShowcase(selectedId)
    await refreshList()
    await selectShowcase(null)
    closeEditor()
  }

  /** `laden = false`: nur hinnavigieren — der Aufrufer lädt selbst (Word-Import). */
  function open(id: string | null, laden = true) {
    const hash = `#/showcases/${id ? encodeURIComponent(id) : 'neu'}`
    // Vom Formular zum nächsten Showcase ersetzen, sonst sammelt die Zurück-Geste jeden Klick.
    if (window.history.state?.k4iaEditor)
      window.history.replaceState({ k4iaEditor: true }, '', hash)
    else window.history.pushState({ k4iaEditor: true }, '', hash)
    setEditing(true)
    window.scrollTo(0, 0)
    if (laden) void selectShowcase(id)
  }

  function closeEditor() {
    if (window.history.state?.k4iaEditor) {
      window.history.back()
    } else {
      window.history.replaceState(null, '', '#/showcases')
      setEditing(false)
    }
  }

  async function handleUpload(file: File, type: AdminAsset['type']) {
    if (!selectedId) {
      alert('Bitte zuerst speichern, bevor Medien hochgeladen werden.')
      return
    }
    // Fehler sammeln sich je Auswahl (gelöscht in onBegin): Bei zehn Fotos
    // soll am Ende dastehen, welche nicht gingen — nicht nur das letzte.
    const fehler = (text: string) =>
      setUploadFehler((alt) => (alt ? `${alt}\n${file.name}: ${text}` : `${file.name}: ${text}`))
    try {
      let datei = file
      // Bilder gehen verkleinert und als JPEG hinauf — damit jeder Browser sie
      // zeigt (HEIC vom iPhone nicht) und die Kamera-Erkennung sie lesen kann.
      if (type === 'image' || type === 'reference_image') {
        const klein = await verkleinereBild(file, type === 'image' ? BILD_KANTE : REFERENZ_KANTE)
        if (!klein) {
          fehler(BILD_NICHT_LESBAR)
          return
        }
        datei = klein
      }
      await uploadAsset(selectedId, datei, type)
      const { assets } = await getShowcase(selectedId)
      setAssets(assets)
    } catch (err) {
      fehler(`Hochladen fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`)
    }
  }

  return (
    <div className="space-y-6">
      <h2 className={`page-title ${editing ? 'hidden lg:block' : ''}`}>Showcases</h2>
      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        <div className={editing ? 'hidden lg:block' : ''}>
          <button
            onClick={() => open(null)}
            className="btn-primary mb-4 flex w-full items-center justify-center gap-2"
          >
            <Plus size={16} strokeWidth={2.5} aria-hidden="true" />
            Neuer Showcase
          </button>
          <button
            type="button"
            onClick={() => void exportieren(null)}
            disabled={export_ !== null || showcases.length === 0}
            className="btn-secondary mb-4 flex w-full items-center justify-center gap-2"
          >
            <FileDown size={16} aria-hidden="true" />
            {export_ && export_.id === null ? export_.text : 'Alle als Word'}
          </button>
          <div className="mb-4">
            <FileButton
              label="Aus Word übernehmen"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              hint="Ein Showcase im Aufbau des Word-Exports"
              onBegin={() => setWordFehler(null)}
              onFile={ausWordUebernehmen}
            />
            {wordFehler && <p className="mt-1 text-sm text-status-error">{wordFehler}</p>}
          </div>
          {exportFehler && <p className="mb-4 text-sm text-status-error">{exportFehler}</p>}
          <ul className="space-y-2 lg:space-y-1">
            {showcases.map((s) => (
              <li key={s.id}>
                <button
                  onClick={() => open(s.id)}
                  className={`flex w-full items-center gap-3 rounded-2xl border-2 px-4 py-3.5 text-left shadow-[var(--shadow-card)] lg:rounded-xl lg:py-3 lg:text-sm ${
                    selectedId === s.id && editing
                      ? 'border-transparent bg-surface lg:border-brand'
                      : 'border-transparent bg-surface'
                  }`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium">{s.title}</span>
                    <span className="block truncate text-xs text-text-muted">
                      {s.status === 'live' ? (
                        <span className="text-status-active">● live</span>
                      ) : (
                        '○ Entwurf'
                      )}{' '}
                      · {s.slug}
                    </span>
                  </span>
                  <ChevronRight
                    size={18}
                    className="shrink-0 text-text-muted lg:hidden"
                    aria-hidden="true"
                  />
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div
          className={`card min-w-0 max-w-3xl space-y-4 p-5 sm:p-6 ${editing ? '' : 'hidden lg:block'}`}
        >
          <BackButton label="Showcases" onClick={closeEditor} />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="section-title">
              {selectedId ? form.title || 'Showcase bearbeiten' : 'Neuer Showcase'}
            </h2>
            {selectedId && (
              <button
                type="button"
                onClick={() => void exportieren(selectedId)}
                disabled={export_ !== null}
                className="btn-chip"
                title="Gespeicherter Stand — ungespeicherte Änderungen fehlen"
              >
                <FileDown size={16} aria-hidden="true" />
                {export_ && export_.id === selectedId ? export_.text : 'Als Word'}
              </button>
            )}
          </div>
          {errorMessage && <p className="text-status-error">{errorMessage}</p>}
          {wordImport && (
            <div
              role="status"
              className="rounded-2xl border border-brand/40 bg-brand/10 px-4 py-3 text-sm text-text"
            >
              <p className="font-semibold">
                Aus „{wordImport.datei}“ übernommen — bitte prüfen, dann Speichern.
              </p>
              {wordImport.hinweise.length > 0 && (
                <ul className="mt-1 list-disc pl-5 text-text-muted">
                  {wordImport.hinweise.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {/* Auch hier: Am Handy ist die Liste im Editor ausgeblendet. */}
          {exportFehler && <p className="text-sm text-status-error">{exportFehler}</p>}

          <Field label="Titel">
            <input
              className="input"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </Field>
          <Field label="Slug (für /s/<slug>)">
            <input
              className="input"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
            />
          </Field>
          <Field label="Kurzbeschreibung">
            <textarea
              className="input"
              rows={2}
              value={form.shortText}
              onChange={(e) => setForm({ ...form, shortText: e.target.value })}
            />
          </Field>
          <Field label="Langtext">
            <textarea
              className="input"
              rows={8}
              value={form.longText}
              onChange={(e) => setForm({ ...form, longText: e.target.value })}
            />
          </Field>
          <Field label="Zielgruppe">
            <input
              className="input"
              value={form.targetAudience}
              onChange={(e) => setForm({ ...form, targetAudience: e.target.value })}
            />
          </Field>
          <Field label="Kernbotschaften (eine pro Zeile)">
            <textarea
              className="input"
              rows={3}
              value={form.keyMessages.join('\n')}
              onChange={(e) =>
                setForm({ ...form, keyMessages: e.target.value.split('\n').filter(Boolean) })
              }
            />
          </Field>
          <Field label="Weitere Einsatzbereiche (einer pro Zeile)">
            <textarea
              className="input"
              rows={3}
              placeholder="z. B. Lebensmittelhandel: Kunde sieht am Regal, was zu seinem Einkauf passt"
              value={form.useCases.join('\n')}
              onChange={(e) =>
                setForm({ ...form, useCases: e.target.value.split('\n').filter(Boolean) })
              }
            />
            <p className="mt-1 text-xs text-text-muted">
              Wofür J4F den Showcase außer dem Gezeigten noch sieht. K4ia nennt das als bestätigt;
              fragt jemand weiter, denkt sie mit — dann aber ausdrücklich als Idee, nie als Kunde,
              Projekt oder Zusage.
            </p>
          </Field>
          <Field label="Nicht-sagen-Regeln (eine pro Zeile)">
            <textarea
              className="input"
              rows={2}
              value={form.tabooRules.join('\n')}
              onChange={(e) =>
                setForm({ ...form, tabooRules: e.target.value.split('\n').filter(Boolean) })
              }
            />
          </Field>
          <FaqEditor value={form.faq} onChange={(faq) => setForm({ ...form, faq })} />
          <Field label="Visuelle Merkmale (für die Kamera-Erkennung, Phase 3)">
            <textarea
              className="input"
              rows={2}
              placeholder="z. B. Tisch-Aufbau mit Kamera über einem Förderband, Bildschirm zeigt Live-Kamerabild mit Markierungsrahmen …"
              value={form.visualDescription}
              onChange={(e) => setForm({ ...form, visualDescription: e.target.value })}
            />
            <p className="mt-1 text-xs text-text-muted">
              Beschreibt den physischen Aufbau, nicht die Marketingbotschaft — hilft K4ia, den
              Showcase per Handykamera zu erkennen.
            </p>
          </Field>
          {/* Kein <Field>: Das ist ein <label>, und Knöpfe darin lösten beim Tippen das Eingabefeld aus. */}
          <div>
            <label
              htmlFor="partner-name"
              className="mb-1 block text-sm font-medium text-text-muted"
            >
              Powered by (optional)
            </label>
            <input
              id="partner-name"
              className="input"
              maxLength={80}
              placeholder="Name des Partners, z. B. Muster GmbH"
              value={form.partnerName}
              onChange={(e) => setForm({ ...form, partnerName: e.target.value })}
            />
            <p className="mt-1 text-xs text-text-muted">
              Name und Logo leer lassen, wenn es keinen Partner gibt. Sonst steht „powered by …“
              unten, sobald K4ia über diesen Showcase erzählt — am Intro-Bildschirm und unter ihren
              Antworten im Chat. Mit Logo steht das Logo statt des Namens.
            </p>
            {selectedId ? (
              <div className="mt-2 flex flex-wrap items-center gap-3">
                {partnerLogo && (
                  <img
                    src={partnerLogoUrl(selectedId, partnerLogo)}
                    alt={`Logo ${form.partnerName || 'Partner'}`}
                    className="h-10 max-w-[10rem] rounded-md bg-white object-contain p-1"
                  />
                )}
                <FileButton
                  label={partnerLogo ? 'Logo ersetzen' : 'Logo hochladen'}
                  accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                  hint="PNG mit transparentem Hintergrund am besten, unter 1 MB, kein SVG"
                  onBegin={() => setLogoFehler(null)}
                  onFile={logoHochladen}
                />
                {partnerLogo && (
                  <button
                    type="button"
                    onClick={() => void logoEntfernen()}
                    className="btn-chip-danger"
                  >
                    Logo entfernen
                  </button>
                )}
              </div>
            ) : (
              <p className="mt-1 text-xs text-text-muted">
                Ein Logo lässt sich nach dem ersten Speichern hochladen.
              </p>
            )}
            {logoFehler && (
              <p role="alert" className="mt-1 text-sm text-status-error">
                {logoFehler}
              </p>
            )}
          </div>
          <div>
            <span className="mb-1 block text-sm font-medium text-text-muted">Ansprechpartner</span>
            <ContactsEditor
              value={form.contactPerson ?? ''}
              onChange={(contactPerson) => setForm({ ...form, contactPerson })}
            />
            <p className="mt-1 text-xs text-text-muted">
              Name für Smenso-Zuweisung und Auskunft, Mailadresse im Tenant (z. B.
              vorname.nachname@j4p.de) für die Teams-Direktnachricht bei Rückfragen. Ohne Mail
              bleibt es bei der Aufgabe in Smenso.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <Field label="Status">
              <select
                className="input"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as 'draft' | 'live' })}
              >
                <option value="draft">Entwurf</option>
                <option value="live">Live</option>
              </select>
            </Field>
            <Field label="Sortierung">
              <input
                type="number"
                className="input"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: Number(e.target.value) })}
              />
            </Field>
          </div>

          <div className="sticky-actions -mx-2 flex items-center gap-2 rounded-full p-2 max-lg:glass max-lg:border max-lg:border-border max-lg:shadow-[var(--shadow-card)] lg:static lg:mx-0 lg:p-0 lg:pt-2">
            <button
              onClick={() => void handleSave()}
              disabled={status === 'saving'}
              className="btn-primary flex-1 lg:flex-none"
            >
              {status === 'saving' ? 'Speichert …' : 'Speichern'}
            </button>
            {selectedId && (
              <button onClick={() => void handleDelete()} className="btn-danger">
                Löschen
              </button>
            )}
            {saved && status === 'idle' && (
              <span role="status" className="hidden text-sm text-status-active sm:inline">
                Gespeichert
              </span>
            )}
          </div>

          {selectedId && (
            <div className="mt-8 border-t border-border pt-6">
              <h3 className="mb-3 font-semibold">Medien</h3>
              {assets.length > 0 && (
                <ul className="mb-4 divide-y divide-border overflow-hidden rounded-2xl bg-surface-2">
                  {assets.map((a) => (
                    <li key={a.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                      <span className="min-w-0 flex-1">
                        <span className="block font-medium">{ASSET_LABEL[a.type]}</span>
                        <span className="block truncate text-xs text-text-muted">
                          {a.r2Key.split('/').pop()}
                        </span>
                      </span>
                      <button
                        onClick={() =>
                          void deleteAsset(selectedId, a.id).then(() => selectShowcase(selectedId))
                        }
                        className="btn-chip-danger shrink-0"
                      >
                        Entfernen
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="grid gap-3 sm:grid-cols-2">
                <FileButton
                  label="Bild"
                  multiple
                  onBegin={() => setUploadFehler(null)}
                  accept="image/*"
                  hint="Zeigt K4ia im Chat; wird verkleinert hochgeladen"
                  onFile={(f) => handleUpload(f, 'image')}
                />
                <FileButton
                  label="Video"
                  multiple
                  onBegin={() => setUploadFehler(null)}
                  accept="video/*"
                  hint="Zeigt K4ia im Chat (MP4, kurz halten)"
                  onFile={(f) => handleUpload(f, 'video')}
                />
                <FileButton
                  label="PDF"
                  multiple
                  onBegin={() => setUploadFehler(null)}
                  accept="application/pdf"
                  hint="Zum Zeigen im Chat — zum Lesen unter „Dokumente“ hochladen"
                  onFile={(f) => handleUpload(f, 'pdf')}
                />
                <FileButton
                  label="Referenzbild"
                  multiple
                  onBegin={() => setUploadFehler(null)}
                  accept="image/*"
                  hint="Für die Kamera-Erkennung: 3 Fotos aus verschiedenen Winkeln"
                  onFile={(f) => handleUpload(f, 'reference_image')}
                />
              </div>
              {uploadFehler && (
                <p role="alert" className="mt-2 whitespace-pre-line text-sm text-status-error">
                  {uploadFehler}
                </p>
              )}

              <div className="mt-6">
                <CameraTest showcaseTitleById={new Map(showcases.map((s) => [s.id, s.title]))} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Ansprechpartner als Liste aus Name und Mail — gespeichert wird weiter der
 * eine Text „Name <mail>, Name2“, damit Import, Smenso und Prompt nichts
 * Neues lernen müssen (parseContacts in @k4ia/shared).
 */
function ContactsEditor({ value, onChange }: { value: string; onChange: (text: string) => void }) {
  // Lokale Zeilen, damit eine leere neue Zeile stehen bleibt, bis etwas
  // getippt ist — im gespeicherten Text gibt es sie erst mit Inhalt.
  const [contacts, setContacts] = useState<Contact[]>(() => parseContacts(value))
  useEffect(() => {
    if (formatContacts(contacts) !== value) setContacts(parseContacts(value))
    // Nur bei Änderung von außen (anderer Showcase); die eigenen Zeilen bleiben.
  }, [value])
  const update = (next: Contact[]) => {
    setContacts(next)
    onChange(formatContacts(next))
  }
  return (
    <div className="space-y-2">
      {contacts.map((c, i) => (
        <div
          key={i}
          className="flex items-start gap-2 rounded-2xl bg-surface-2/50 p-2 sm:bg-transparent sm:p-0"
        >
          <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-2">
            <input
              className="input"
              placeholder="Name"
              value={c.name}
              onChange={(e) =>
                update(contacts.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)))
              }
            />
            <input
              className="input"
              type="email"
              placeholder="mail@j4p.de (für Teams)"
              value={c.email ?? ''}
              onChange={(e) =>
                update(
                  contacts.map((x, k) =>
                    k === i ? { ...x, email: e.target.value.trim() || undefined } : x,
                  ),
                )
              }
            />
          </div>
          <button
            type="button"
            aria-label="Ansprechpartner entfernen"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-text-muted hover:text-status-error"
            onClick={() => update(contacts.filter((_, k) => k !== i))}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn-chip"
        onClick={() => update([...contacts, { name: '' }])}
      >
        <Plus size={15} aria-hidden="true" /> Ansprechpartner
      </button>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-text-muted">{label}</span>
      {children}
    </label>
  )
}

function FaqEditor({
  value,
  onChange,
}: {
  value: FaqEntry[]
  onChange: (faq: FaqEntry[]) => void
}) {
  return (
    <div>
      <span className="mb-1 block text-sm font-medium text-text-muted">FAQ</span>
      <div className="space-y-2">
        {value.map((entry, i) => (
          <div
            key={i}
            className="flex items-start gap-2 rounded-2xl bg-surface-2/50 p-2 sm:bg-transparent sm:p-0"
          >
            <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-2">
              <input
                className="input"
                placeholder="Frage"
                value={entry.question}
                onChange={(e) => {
                  const next = [...value]
                  next[i] = { ...entry, question: e.target.value }
                  onChange(next)
                }}
              />
              <input
                className="input"
                placeholder="Antwort"
                value={entry.answer}
                onChange={(e) => {
                  const next = [...value]
                  next[i] = { ...entry, answer: e.target.value }
                  onChange(next)
                }}
              />
            </div>
            <button
              type="button"
              aria-label="Frage entfernen"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-text-muted hover:text-status-error"
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange([...value, { question: '', answer: '' }])}
        className="btn-chip mt-2"
      >
        <Plus size={15} aria-hidden="true" /> Frage hinzufügen
      </button>
    </div>
  )
}

const ASSET_LABEL: Record<AdminAsset['type'], string> = {
  image: 'Bild',
  video: 'Video',
  pdf: 'PDF',
  reference_image: 'Referenzbild',
}
